import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../config/api';
import { QuickStats } from '../components/dashboard/QuickStats';

interface DashboardPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
  onNavigate?: (viewId: string) => void;
}

const reduceMeals = (meals: any[]) =>
  meals.reduce(
    (acc: any, meal: any) => ({
      calories: acc.calories + (meal.totals?.calories || 0),
      protein: acc.protein + (meal.totals?.protein || 0),
      carbohydrates: acc.carbohydrates + (meal.totals?.carbs || 0),
      fat: acc.fat + (meal.totals?.fat || 0),
      meal_count: acc.meal_count + 1,
    }),
    { calories: 0, protein: 0, carbohydrates: 0, fat: 0, meal_count: 0 }
  );

export const DashboardPage: React.FC<DashboardPageProps> = ({ user, coachMode, clientId, onNavigate }) => {
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  const fetchMeals = async (date: string) => {
    const data = await apiClient.getMealPortfolioByDate(date, coachMode ? clientId : undefined);
    return reduceMeals(data.portfolio || []);
  };

  const { data: todayData, isLoading: todayLoading } = useQuery({
    queryKey: coachMode ? ['client-today-meals', clientId, today] : ['today-meals', user?.id, today],
    queryFn: () => fetchMeals(today),
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  const { data: yesterdayData, isLoading: yesterdayLoading } = useQuery({
    queryKey: coachMode ? ['client-yesterday-meals', clientId, yesterday] : ['yesterday-meals', user?.id, yesterday],
    queryFn: () => fetchMeals(yesterday),
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  const { data: nutritionData, isLoading: nutritionLoading } = useQuery({
    queryKey: coachMode ? ['client-7day-meals', clientId] : ['7day-meals', user?.id],
    queryFn: async () => {
      const data = await apiClient.getMealPortfolioByDays(8, coachMode ? clientId : undefined);
      const allMeals = data.portfolio || [];

      const todayStr = new Date().toISOString().split('T')[0];
      const previousMeals = allMeals.filter((meal: any) => {
        const mealDate = new Date(meal.meal_date || meal.timestamp || meal.created_at).toISOString().split('T')[0];
        return mealDate !== todayStr;
      });

      const totals = reduceMeals(previousMeals);
      return {
        meal_count: totals.meal_count,
        average_per_day: {
          calories: totals.calories / 7,
          protein: totals.protein / 7,
          carbohydrates: totals.carbohydrates / 7,
          fat: totals.fat / 7,
        },
      };
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  const goalsData = { goals: [] };

  if (nutritionLoading || todayLoading || yesterdayLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-200 rounded-lg h-32 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Navigation cards config
  const navCards = [
    { viewId: 'nutrition', path: '/nutrition-overview', icon: '🥗', color: 'blue', title: 'Nutrition Overview', subtitle: 'Comprehensive 44-nutrient analysis' },
    { viewId: 'goals', path: '/goals-progress', icon: '🎯', color: 'green', title: 'Goals & Progress', subtitle: 'Client goals and tracking' },
    { viewId: 'client-activity', path: '/client-activity', icon: '👥', color: 'green', title: 'Client Activity', subtitle: 'Monitor client engagement and progress' },
    { viewId: 'reports', path: '/reports', icon: '📧', color: 'orange', title: 'Reports', subtitle: 'Email templates and insights' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white">
                {coachMode ? 'Client:' : 'Dashboard:'} {user?.full_name || user?.fullName || user?.email || 'Unknown'}
              </h1>
              <p className="mt-2 text-blue-100">
                Nutrition analysis overview for the past 7 days
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">
                {(nutritionData as any)?.meal_count || '0'}
              </div>
              <div className="text-blue-100 text-sm">
                meals logged
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <QuickStats
        nutritionData={nutritionData}
        goalsData={goalsData}
        todayData={todayData}
        yesterdayData={yesterdayData}
      />

      {/* Quick Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {navCards.map((card) => {
          const inner = (
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 bg-${card.color}-100 rounded-lg flex items-center justify-center`}>
                    <span className={`text-${card.color}-600`}>{card.icon}</span>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">{card.title}</h3>
                  <p className="text-sm text-gray-500">{card.subtitle}</p>
                </div>
              </div>
            </div>
          );

          // In coach mode use onNavigate callback; otherwise use router Link
          if (onNavigate) {
            return (
              <button
                key={card.viewId}
                onClick={() => onNavigate(card.viewId)}
                className="card hover:shadow-lg transition-shadow cursor-pointer text-left"
              >
                {inner}
              </button>
            );
          }
          return (
            <Link key={card.viewId} to={card.path} className="card hover:shadow-lg transition-shadow cursor-pointer">
              {inner}
            </Link>
          );
        })}
      </div>
    </div>
  );
};
